import { useCallback, useEffect, useRef, type CSSProperties } from 'react';
import L from 'leaflet';
import { BEACHES, type Beach } from '../data/beaches';
import type { BeachConditions, RankedBeach } from '../lib/forecast';
import { compass, scoreColor, verdict } from '../lib/scoring';

const CENTER: L.LatLngTuple = [34.965, 33.87];
const BOUNDS = L.latLngBounds(BEACHES.map((b) => [b.lat, b.lon] as L.LatLngTuple));
const n = (v: number | null, digits = 2) => (v == null ? '–' : v.toFixed(digits));
const nRound = (v: number | null) => (v == null ? '–' : Math.round(v));

function markerHtml(score: number | null, isBest: boolean, isSel: boolean): string {
  const cls = `divemark${isBest ? ' best' : ''}${isSel ? ' sel' : ''}`;
  return `<div class="${cls}" style="background:${scoreColor(score)}">${
    score == null ? '–' : Math.round(score)
  }</div>`;
}

function popupHtml(b: Beach, d: BeachConditions): string {
  const [vTxt] = verdict(d.score);
  return `<div class="pn">${b.name} <span style="color:${scoreColor(d.score)}">${
    d.score == null ? '' : `${d.score.toFixed(1)}/10`
  }</span></div>
    <div class="pd">${vTxt}<br>
    eff. wave: <b>${n(d.effH)} m</b> (offshore ${n(d.rawH)} m from ${compass(d.dir)})<br>
    period ${n(d.per, 0)} s · exposure ×${d.exp.toFixed(2)}<br>
    wind ${nRound(d.wind)} km/h ${compass(d.wdir)}, gusts ${nRound(d.gust)}</div>`;
}

interface Props {
  ranked: RankedBeach[];
  /** bump `nonce` to re-trigger a pan+popup even for the same beach */
  focus: { id: string; nonce: number } | null;
  selectedId: string | null;
  /** px of map hidden behind the bottom sheet — focus pans above it */
  bottomInset: number;
  compact: boolean;
  onSelectBeach: (beachId: string) => void;
}

export default function WaveMap({
  ranked,
  focus,
  selectedId,
  bottomInset,
  compact,
  onSelectBeach,
}: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const insetRef = useRef(bottomInset);
  insetRef.current = bottomInset;
  const selectRef = useRef(onSelectBeach);
  selectRef.current = onSelectBeach;

  useEffect(() => {
    if (!containerRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: false }).setView(CENTER, 11);
    map.attributionControl.setPrefix('');
    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 18,
      attribution: '&copy; OpenStreetMap contributors',
    }).addTo(map);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      markersRef.current = {};
    };
  }, []);

  /* zoom buttons only where there is room and no pinch gesture */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || compact) return;
    const zoom = L.control.zoom({ position: 'topleft' });
    zoom.addTo(map);
    return () => {
      zoom.remove();
    };
  }, [compact]);

  /* the visible slice of the map changes as the sheet moves */
  useEffect(() => {
    mapRef.current?.invalidateSize({ animate: false });
  }, [bottomInset, compact]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    ranked.forEach(({ beach, conditions }, pos) => {
      const isBest = pos === 0;
      const isSel = beach.id === selectedId;
      const big = isBest || isSel;
      const side = big ? 42 : 34;
      const icon = L.divIcon({
        html: markerHtml(conditions.score, isBest, isSel),
        className: '',
        iconSize: [side, side],
        iconAnchor: [side / 2, side / 2],
      });
      const popup = popupHtml(beach, conditions);
      const existing = markersRef.current[beach.id];
      if (existing) {
        existing.setIcon(icon);
        existing.setPopupContent(popup);
        existing.setZIndexOffset(big ? 1000 : 0);
      } else {
        const marker = L.marker([beach.lat, beach.lon], { icon, zIndexOffset: big ? 1000 : 0 })
          .addTo(map)
          .bindPopup(popup, { maxWidth: 260, autoPanPadding: [16, 16], closeButton: true });
        marker.on('click', () => selectRef.current(beach.id));
        markersRef.current[beach.id] = marker;
      }
    });
  }, [ranked, selectedId]);

  useEffect(() => {
    if (!focus) return;
    const map = mapRef.current;
    const marker = markersRef.current[focus.id];
    const beach = BEACHES.find((b) => b.id === focus.id);
    if (!map || !marker || !beach) return;
    map.setView([beach.lat, beach.lon], Math.max(map.getZoom(), 13), { animate: true });
    // re-centre into the strip of map still visible above the sheet
    if (insetRef.current > 0) map.panBy([0, insetRef.current / 2], { animate: false });
    marker.openPopup();
  }, [focus]);

  const fitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    map.closePopup();
    map.fitBounds(BOUNDS, {
      paddingTopLeft: [28, 28],
      paddingBottomRight: [28, insetRef.current + 28],
    });
  }, []);

  return (
    // --map-inset lifts the attribution and the fit-all button clear of the sheet
    <div id="mapwrap" style={{ '--map-inset': `${bottomInset}px` } as CSSProperties}>
      <div id="map" ref={containerRef} />
      <button
        type="button"
        className="mapfab"
        onClick={fitAll}
        aria-label="Show all beaches"
        title="Show all beaches"
      >
        ⤢
      </button>
    </div>
  );
}
